import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { AccountSelectors, CurrentUserSelectors } from '@core/account-data';
import { User } from '@core/account-types';
import { IntegrationsInsertsService } from '@core/integrations-logic';
import { TriggerCategory } from '@core/workflow-automations-types';
import { AutomationsJobItem } from '@recruitee/workflow-automations-types';

@Injectable()
export class AutomationsAccessoryDataService {
  private readonly store = inject(Store);
  private readonly integrationsInsertsService = inject(IntegrationsInsertsService);

  public isLockedStage = signal(false);
  public allowedUsers = computed(() => this.groupedUsers()[this._triggerCategory()].allowedUsers);
  public disallowedUsers = computed(
    () => this.groupedUsers()[this._triggerCategory()].disallowedUsers,
  );
  public jobItemData = signal<AutomationsJobItem | null>(null);
  public currentUser = this.store.selectSignal(CurrentUserSelectors.data);
  public enabledIntegrations = toSignal(
    this.integrationsInsertsService.getEnabledIntegrationKids(),
  );
  public companyName = this.store.selectSignal(AccountSelectors.currentCompanyName);

  private groupedUsers = signal<
    Record<TriggerCategory, { allowedUsers: User[]; disallowedUsers: User[] }>
  >({
    [TriggerCategory.Candidate]: { allowedUsers: [], disallowedUsers: [] },
    [TriggerCategory.Job]: { allowedUsers: [], disallowedUsers: [] },
  });
  private _triggerCategory = signal<TriggerCategory>(TriggerCategory.Candidate);

  public setGroupedUsers(
    users: Record<TriggerCategory, { allowedUsers: User[]; disallowedUsers: User[] }>,
  ): void {
    this.groupedUsers.set(users);
  }

  public setTriggerCategory(category: TriggerCategory): void {
    this._triggerCategory.set(category);
  }

  public get triggerCategory() {
    return this._triggerCategory.asReadonly();
  }

  public setJobItemData(jobData: AutomationsJobItem | null): void {
    this.jobItemData.set(jobData);
  }
}
